import { apiClient } from '@/lib/api-client';
import type { PaginatedResponse } from '@/types/contractor';
import type {
  CreateListingInput,
  Listing,
  ListingQueryParams,
  UpdateListingInput,
} from '@/types/listing';

// API wraps responses in result object
interface ApiWrapper<T> {
  result: T;
  status: number;
  message: string;
  error: null | { code: number; message: string; issues: unknown[] };
}

export const listingService = {
  /**
   * Get all listings with pagination and filters
   */
  async getAll(params?: ListingQueryParams): Promise<PaginatedResponse<Listing>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.city) queryParams.set('city', params.city);
    if (params?.state) queryParams.set('state', params.state);
    if (params?.minPrice) queryParams.set('minPrice', String(params.minPrice));
    if (params?.maxPrice) queryParams.set('maxPrice', String(params.maxPrice));
    if (params?.minBeds) queryParams.set('minBeds', String(params.minBeds));
    if (params?.minBaths) queryParams.set('minBaths', String(params.minBaths));
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = `/listings${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<ApiWrapper<PaginatedResponse<Listing>>>(endpoint);
    return response.result;
  },

  /**
   * Get listing by ID with populated relations
   */
  async getById(id: string): Promise<Listing> {
    const response = await apiClient.get<ApiWrapper<Listing>>(`/listings/${id}`);
    return response.result;
  },

  /**
   * Create a new listing
   */
  async create(data: CreateListingInput): Promise<Listing> {
    const response = await apiClient.post<ApiWrapper<Listing>>('/listings', data);
    return response.result;
  },

  /**
   * Update a listing
   */
  async update(id: string, data: UpdateListingInput): Promise<Listing> {
    const response = await apiClient.put<ApiWrapper<Listing>>(`/listings/${id}`, data);
    return response.result;
  },

  /**
   * Delete (soft delete) a listing
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/listings/${id}`);
  },

  /**
   * Update listing status
   */
  async updateStatus(id: string, status: string): Promise<Listing> {
    const response = await apiClient.put<ApiWrapper<Listing>>(`/listings/${id}`, { status });
    return response.result;
  },
};
